import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Progress, Alert, Spin, Empty, Space, Typography, Tag, Table, Button, message } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, SyncOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ListPageHeader from '@shared/ListPageHeader';
import OrderStatusLabel from '@shared/orders/OrderStatusLabel';
import { useMerchant } from '@contexts/admin/MerchantContext';
import { adminApiFetch } from '@services/admin/apiService';
import dayjs from 'dayjs';

const { Text } = Typography;

const SlaPerformance = () => {
  const { t } = useTranslation();
  const { currentMerchant, loading: merchantLoading } = useMerchant();
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [data, setData] = useState(null);
  const [overdueOrders, setOverdueOrders] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const isSlaEnabled = currentMerchant?.sla_enabled;

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApiFetch('analytics/sla/summary');
      const result = await response.json();
      if (result.success) {
        setData(result.data);
      } else {
        message.error(result.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch SLA summary:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchOverdueOrders = useCallback(async (page = 1, pageSize = 10) => {
    setTableLoading(true);
    try {
      const response = await adminApiFetch(`analytics/sla/overdue_orders?page=${page}&page_size=${pageSize}&with_count=true`);
      const result = await response.json();
      if (result.success) {
        setOverdueOrders(result.data);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: result.total_count || 0,
        });
      } else {
        message.error(result.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch overdue orders:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setTableLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!currentMerchant?.id) return;
    if (isSlaEnabled) {
      fetchSummary();
      fetchOverdueOrders(1, pagination.pageSize);
    } else {
      setData(null);
      setOverdueOrders([]);
    }
  }, [currentMerchant?.id, isSlaEnabled]);

  const handleTableChange = (newPagination) => {
    fetchOverdueOrders(newPagination.current, newPagination.pageSize);
  };

  const handleRefresh = () => {
    fetchSummary();
    fetchOverdueOrders(1, pagination.pageSize);
  };

  if (merchantLoading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (loading && !data && isSlaEnabled) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const isComplianceLow = data?.compliance_rate < data?.target_percentage;
  const cutoffTime = data?.sla_cutoff_time || currentMerchant?.sla_cutoff_time || '-';
  // SLA 已啟用但尚無追蹤數據（歷史訂單都在啟用前建立）
  const hasNoTrackingData = isSlaEnabled && data && data.sla_tracked_orders === 0 && data.total_orders > 0;

  const columns = [
    {
      title: t('models.order.name'),
      dataIndex: 'name',
      render: (name, record) => <a href={`/admin/orders/${record.id}`}>{name}</a>,
    },
    {
      title: t('models.order.status'),
      dataIndex: 'status',
      width: 120,
      render: (status) => <OrderStatusLabel status={status} />,
    },
    {
      title: t('models.order.expected_ship_date'),
      dataIndex: 'expected_ship_date',
      width: 140,
    },
    {
      title: t('common.days_overdue'),
      dataIndex: 'days_overdue',
      width: 120,
      sorter: (a, b) => a.days_overdue - b.days_overdue,
      render: (days) => (
        <Tag color={days >= 3 ? 'red' : days >= 1 ? 'orange' : 'default'}>
          {days} {t('common.label.day', { count: days, defaultValue: 'day(s)' })}
        </Tag>
      ),
    },
    {
      title: t('models.order.placed_at'),
      dataIndex: 'placed_at',
      width: 160,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-',
    },
  ];

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      <ListPageHeader
        basePath="/admin"
        items={['analytics.title', 'analytics.sla']}
      />

      <div style={{ padding: '0 24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Info Bar */}
          <Card size="small">
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <ClockCircleOutlined />
                  <Text strong>{t('shared.orders.list.filters.date_range')}:</Text>
                  <Text>{dayjs().subtract(30, 'days').format('YYYY-MM-DD')} ~ {dayjs().format('YYYY-MM-DD')}</Text>
                  <Tag color="blue">{t('common.last_30_days')}</Tag>
                </Space>
              </Col>
              <Col>
                <Space size="middle">
                  <Text type="secondary">
                    {t('admin.settings.general.sla_settings.cutoff_time')}: <b>{cutoffTime}</b>
                  </Text>
                  {isSlaEnabled && (
                    <Button
                      type="link"
                      icon={<SyncOutlined />}
                      onClick={handleRefresh}
                      loading={loading || tableLoading}
                    />
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {!isSlaEnabled ? (
            <Alert
              message={t('admin.settings.general.sla_settings.disabled')}
              description={t('admin.settings.general.sla_settings.description')}
              type="warning"
              showIcon
            />
          ) : (
            <>
              {/* 有訂單但尚無 SLA 追蹤數據 */}
              {hasNoTrackingData && (
                <Alert
                  message={t('admin.settings.general.sla_settings.no_tracking_data')}
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              )}

              {/* SLA 追蹤覆蓋率提示 */}
              {data && data.sla_tracked_orders > 0 && data.sla_tracked_orders < data.total_orders && (
                <Alert
                  message={t('admin.settings.general.sla_settings.tracking_coverage', {
                    tracked: data.sla_tracked_orders,
                    total: data.total_orders,
                  })}
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              )}

              {/* KPI Cards */}
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Card bordered={false}>
                    <Statistic
                      title={
                        <Space>
                          {t('models.order._')}
                          <Tag color="blue" style={{ fontWeight: 'normal' }}>{t('common.last_30_days')}</Tag>
                        </Space>
                      }
                      value={data?.total_orders ?? '-'}
                      prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card bordered={false}>
                    <Statistic
                      title={t('common.on_time')}
                      value={data?.shipped_on_time ?? '-'}
                      valueStyle={{ color: '#52c41a' }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card bordered={false}>
                    <Statistic
                      title={t('common.overdue_shipped')}
                      value={data?.overdue_shipped ?? '-'}
                      valueStyle={{ color: data?.overdue_shipped > 0 ? '#faad14' : undefined }}
                      prefix={<WarningOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card bordered={false}>
                    <Statistic
                      title={t('common.overdue_pending')}
                      value={data?.currently_overdue ?? '-'}
                      valueStyle={{ color: data?.currently_overdue > 0 ? '#ff4d4f' : undefined }}
                      prefix={<ExclamationCircleOutlined />}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Compliance Rate */}
              <Card size="small">
                <Row align="middle" gutter={24}>
                  <Col>
                    <Progress
                      type="circle"
                      percent={data?.compliance_rate ?? 0}
                      width={80}
                      strokeColor={isComplianceLow ? '#faad14' : '#52c41a'}
                      format={(percent) => `${percent}%`}
                    />
                  </Col>
                  <Col>
                    <Space direction="vertical" size={0}>
                      <Text strong style={{ fontSize: 16 }}>{t('common.compliance_rate')}</Text>
                      <Text type="secondary">
                        {t('admin.merchants.fields.sla_target_percentage')}: {data?.target_percentage ?? '-'}%
                      </Text>
                      {isComplianceLow && data?.sla_tracked_orders > 0 && (
                        <Tag color="warning" style={{ marginTop: 4 }}>
                          {t('common.overdue')} - {t('admin.merchants.fields.sla_target_percentage')} {data?.target_percentage}%
                        </Tag>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* Overdue Orders Table */}
              <Card
                title={
                  <Space>
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                    {t('common.overdue_pending')} {t('models.order._')}
                    {data?.currently_overdue > 0 && (
                      <Tag color="red">{data.currently_overdue}</Tag>
                    )}
                  </Space>
                }
              >
                <Table
                  dataSource={overdueOrders}
                  rowKey="id"
                  loading={tableLoading}
                  columns={columns}
                  pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => t('common.table.total_items', { count: total }),
                  }}
                  onChange={handleTableChange}
                  locale={{ emptyText: <Empty description={t('common.table.no_data')} /> }}
                  size="small"
                />
              </Card>
            </>
          )}
        </Space>
      </div>
    </div>
  );
};

export default SlaPerformance;
